import {Helmet} from '@dr.pogodin/react-helmet';
import {Button, openDeleteDialog, openDialog, showNotification} from 'platform/components';
import {Heading, HStack} from 'platform/foundation';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray, isNotNil} from 'ramda-adjunct';

import {useGetWatermarkQuery} from '@dms/api/core';
import {
  useDeleteWatermarkMutation,
  useGetAdvertisingStatisticsQuery,
  useLazyGetWatermarkListQuery,
} from '@dms/api/sales';
import i18n from '@dms/i18n';
import {settingsRoutes} from '@dms/routes';
import {SettingsTemplate} from '@dms/settings/shared';
import {DmsDataGrid, formatDocumentTitle, handleApiError, refreshDmsDatagrid} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

import {openRepublishInfoDialog} from '../../components/RepublishInfoDialogContent/openRepublishInfoDialog';
import {WatermarkDeleteDialog} from './components/WatermarkDeleteDialog';

export function PhotosWatermarking() {
  const {isLoading: isGetWatermarkQueryLoading} = useGetWatermarkQuery();
  const navigate = useNavigate();
  const [deleteWatermark] = useDeleteWatermarkMutation();
  const [getWatermarkList] = useLazyGetWatermarkListQuery();
  const {data: advertisingStatistics, isLoading: isGetAdvertisingStatisticsQueryLoading} =
    useGetAdvertisingStatisticsQuery(undefined, {
      refetchOnMountOrArgChange: true,
    });
  const isLoading = isGetWatermarkQueryLoading || isGetAdvertisingStatisticsQueryLoading;

  const handleActionCallback: ActionCallback = ({actionKey, rowId, refreshData}) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      showNotification.error();
      return;
    }

    match(actionKey)
      .with('edit', () =>
        navigate(composePath(settingsRoutes.advertisingWatermarkingDetail, {params: {id}}))
      )
      .with('delete', () => {
        getWatermarkList()
          .unwrap()
          .then((watermarkList) => {
            if (watermarkList?.length === 1) {
              openDeleteDialog({
                onConfirm: () =>
                  deleteWatermark({
                    watermarkId: id,
                    deleteWatermarkRequestBody: {republishAds: true},
                  })
                    .unwrap()
                    .then(() => refreshData())
                    .then(() => {
                      if (
                        isNotNil(advertisingStatistics?.inAdvertisementCount) &&
                        advertisingStatistics.inAdvertisementCount > 0
                      ) {
                        openRepublishInfoDialog();
                      } else {
                        showNotification.success(
                          i18n.t('entity.watermark.deleteNotification.success')
                        );
                      }
                    })
                    .catch(handleApiError),
                text: i18n.t('page.salesSettings.labels.watermarks.delete'),
              });
            } else {
              openDialog(
                <WatermarkDeleteDialog
                  inAdvertisementCount={advertisingStatistics?.inAdvertisementCount}
                  watermarkId={id}
                />,
                {
                  id: 'deleteWatermark-newWatermark',
                  title: i18n.t('entity.watermark.deleteDialog.title'),
                  'data-testid': 'deleteWatermark-newWatermark-dialog',
                  size: 'small',
                  onCloseComplete: () => {
                    refreshDmsDatagrid('watermark');
                  },
                }
              );
            }
          })
          .catch(handleApiError);
      })
      .otherwise(() => showNotification.warning('unknown action key'));
  };

  return (
    <>
      <Helmet
        title={formatDocumentTitle(
          i18n.t('page.salesSettings.labels.watermarks.name'),
          i18n.t('page.salesSettings.labels.advertising'),
          i18n.t('page.settings.labels.title')
        )}
      />
      <SettingsTemplate
        isLoading={isLoading}
        header={{
          title: i18n.t('page.salesSettings.labels.watermarks.name'),
          breadcrumbs: [
            {
              label: i18n.t('page.salesSettings.labels.advertising'),
              href: settingsRoutes.advertising,
            },
          ],
        }}
        description={i18n.t('page.salesSettings.labels.watermarks.description')}
        data-testid="settings-advertising-watermarking"
      >
        <HStack align="center" justify="space-between">
          <Heading size={4}>
            {i18n.t('page.salesSettings.backgroundRemoval.configurationList')}
          </Heading>
          <Button
            leftIcon="content/add"
            title={i18n.t('page.salesSettings.backgroundRemoval.addConfiguration')}
            variant="link"
            onClick={() => navigate(settingsRoutes.advertisingWatermarkingCreate)}
            data-testid="settings-advertising-watermarking-createConfiguration"
          />
        </HStack>
        <DmsDataGrid
          gridCode="watermark"
          actionCallback={handleActionCallback}
          data-testid="settings-advertising-watermarking-datagrid"
        />
      </SettingsTemplate>
    </>
  );
}
